import { useCallback, useMemo, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Alert, Button, Progress, Tag, Tooltip, message } from 'antd';
import {
  ArrowLeftOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  DeleteOutlined,
  DownloadOutlined,
  FileExcelOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import AppBreadcrumb from '../Breadcrumb';
import {
  DEFAULT_ACCEPT,
  DEFAULT_MAX_SIZE_MB,
  buildAcceptAttr,
  formatFileSize,
  validateBulkFile,
} from './bulkUploadValidation';
import '../../styles/bulk-upload-panel.css';

// ───────────────────────────────────────────────────────────────────────────
// BulkUploadPanel — the shared "download the template, drop the filled sheet
// back in" screen. Any module that imports rows from a spreadsheet (billing
// actuals, test steps, candidates, …) renders this and supplies only its two
// async calls plus a result renderer, so every bulk-upload screen in the suite
// looks and validates identically.
//
// The upload target is a drop area rather than a button: a file can be dragged
// onto it or clicked to browse. Both paths funnel through the one validator in
// bulkUploadValidation.js, so a .pdf or .jpeg never reaches onUpload.
//
// Props
//   title / description        page heading
//   breadcrumbItems            [{ label, href }] — passed to core Breadcrumb
//   backTo / backLabel         optional back link in the header
//   templateTitle / …Hint      right-rail sample-template card copy
//   templateLabel              download button label
//   instructions               string[] rendered as the "Before you upload" list
//   accept                     allowed extensions, default .xlsx/.xls/.csv
//   maxSizeMB                  size ceiling, default 10
//   onDownloadTemplate()       async; triggers the browser download
//   onUpload(file)             async; resolves with the import summary
//   renderResult(summary, {reset})   module-specific summary UI
//   extraSidebar               extra nodes appended to the right rail
// ───────────────────────────────────────────────────────────────────────────

// The drop-area artwork: a spreadsheet with an upload badge. Inline SVG rather
// than a PNG so it stays crisp at any DPI, follows the drop state's colors,
// and adds no asset the host app has to serve.
function UploadArtwork() {
  return (
    <svg className="bulk-upload-art" viewBox="0 0 72 72" focusable="false" aria-hidden="true">
      <rect x="12" y="6" width="36" height="48" rx="5" className="bulk-upload-art__sheet" />
      <path d="M20 20h20M20 28h20M20 36h12" className="bulk-upload-art__lines" />
      <circle cx="50" cy="48" r="15" className="bulk-upload-art__badge" />
      <path d="M50 55V41m0 0-6 6m6-6 6 6" className="bulk-upload-art__arrow" />
    </svg>
  );
}

export default function BulkUploadPanel({
  title,
  description,
  breadcrumbItems,
  backTo,
  backLabel = 'Back',
  templateLabel = 'Download Sample Template',
  templateTitle = 'Sample template',
  templateHint = 'Pre-filled with the exact column names the importer expects. Do not rename or reorder the columns.',
  instructions = [],
  accept = DEFAULT_ACCEPT,
  maxSizeMB = DEFAULT_MAX_SIZE_MB,
  onDownloadTemplate,
  onUpload,
  renderResult,
  extraSidebar,
}) {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [file, setFile] = useState(null);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  // Bumped after every attempt so the <input> remounts empty — picking the
  // SAME file twice in a row still fires onChange.
  const [inputKey, setInputKey] = useState(0);

  const acceptList = useMemo(() => accept.map((item) => item.toLowerCase()), [accept]);
  const acceptAttr = useMemo(() => buildAcceptAttr(acceptList), [acceptList]);

  const reset = useCallback(() => {
    setFile(null);
    setError(null);
    setResult(null);
    setDragging(false);
    setInputKey((key) => key + 1);
  }, []);

  const handleDownload = useCallback(async () => {
    if (!onDownloadTemplate) return;
    setDownloading(true);
    try {
      await onDownloadTemplate();
      message.success('Sample template downloaded.');
    } catch (err) {
      message.error(err?.message || 'Could not download the template.');
    } finally {
      setDownloading(false);
    }
  }, [onDownloadTemplate]);

  // Single entry point for both the browse dialog and drag-and-drop, so the
  // two paths can never diverge on what they let through.
  const acceptFile = useCallback(
    async (picked) => {
      if (!picked || uploading) return;
      setInputKey((key) => key + 1);

      const reason = validateBulkFile(picked, { accept: acceptList, maxSizeMB });
      if (reason) {
        setFile(null);
        setResult(null);
        setError(reason);
        return;
      }

      setError(null);
      setResult(null);
      setFile({ name: picked.name, size: picked.size });
      setUploading(true);
      try {
        const summary = await onUpload(picked);
        setResult(summary);
      } catch (err) {
        setError(err?.message || 'Upload failed. Please try again.');
      } finally {
        setUploading(false);
      }
    },
    [acceptList, maxSizeMB, onUpload, uploading],
  );

  const openFileDialog = () => {
    if (!uploading) inputRef.current?.click();
  };

  const dropzoneClass = [
    'bulk-upload-dropzone',
    dragging ? 'is-dragging' : '',
    uploading ? 'is-busy' : '',
    error ? 'is-error' : '',
    result && !error ? 'is-done' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className="bulk-upload-panel">
      {breadcrumbItems?.length ? (
        <div className="bulk-upload-breadcrumb">
          <AppBreadcrumb items={breadcrumbItems} />
        </div>
      ) : null}

      {/* One bordered frame around the whole working area, matching the outer
          panel the Billing / Payroll list pages sit in (.finance-page). */}
      <div className="bulk-upload-frame">
        <header className="bulk-upload-header">
          <div className="bulk-upload-header__icon" aria-hidden="true">
            <FileExcelOutlined />
          </div>
          <div className="bulk-upload-header__text">
            <h1 className="bulk-upload-header__title">{title}</h1>
            {description ? <p className="bulk-upload-header__desc">{description}</p> : null}
          </div>
          {backTo ? (
            <RouterLink className="bulk-upload-header__back" to={backTo}>
              <Button type="text" icon={<ArrowLeftOutlined />}>{backLabel}</Button>
            </RouterLink>
          ) : null}
        </header>

        <div className="bulk-upload-grid">
          <section className="bulk-upload-card bulk-upload-card--drop">
            <div className="bulk-upload-card__head">
              <span className="bulk-upload-step">Step 2</span>
              <h2 className="bulk-upload-card__title">Upload the filled file</h2>
            </div>

            <div
              role="button"
              tabIndex={uploading ? -1 : 0}
              aria-disabled={uploading}
              aria-label="Upload the filled file — click to browse, or drop a file here"
              className={dropzoneClass}
              onClick={openFileDialog}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  openFileDialog();
                }
              }}
              onDragOver={(event) => {
                event.preventDefault();
                if (!uploading) setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={(event) => {
                event.preventDefault();
                setDragging(false);
                acceptFile(event.dataTransfer.files?.[0]);
              }}
            >
              <UploadArtwork />
              <p className="bulk-upload-dropzone__title">
                {dragging ? 'Drop the file to start uploading' : 'Drag & drop your file here'}
              </p>
              <p className="bulk-upload-dropzone__sub">
                or <span className="bulk-upload-dropzone__link">browse from your computer</span>
              </p>
              <div className="bulk-upload-chips">
                {acceptList.map((ext) => (
                  <Tag key={ext} className="bulk-upload-chip bulk-upload-chip--ok">{ext}</Tag>
                ))}
                <Tooltip title="These file types are rejected before upload">
                  <Tag className="bulk-upload-chip bulk-upload-chip--no">.pdf .jpg .png not allowed</Tag>
                </Tooltip>
              </div>
              <p className="bulk-upload-dropzone__limit">Maximum size {maxSizeMB} MB</p>
            </div>

            <input
              key={inputKey}
              ref={inputRef}
              hidden
              type="file"
              accept={acceptAttr}
              onChange={(event) => acceptFile(event.target.files?.[0])}
            />

            {file ? (
              <div className={`bulk-upload-file ${error ? 'is-error' : ''}`}>
                <span className="bulk-upload-file__icon">
                  {uploading ? <FileExcelOutlined /> : error ? <CloseCircleFilled /> : <CheckCircleFilled />}
                </span>
                <span className="bulk-upload-file__meta">
                  <span className="bulk-upload-file__name" title={file.name}>{file.name}</span>
                  <span className="bulk-upload-file__size">
                    {formatFileSize(file.size)}
                    {uploading ? ' · uploading…' : error ? ' · failed' : ' · processed'}
                  </span>
                </span>
                {uploading ? null : (
                  <Button
                    type="text"
                    size="small"
                    icon={result ? <ReloadOutlined /> : <DeleteOutlined />}
                    onClick={reset}
                  >
                    {result ? 'Upload another' : 'Remove'}
                  </Button>
                )}
              </div>
            ) : null}

            {uploading ? (
              <Progress percent={99} status="active" showInfo={false} size="small" className="bulk-upload-progress" />
            ) : null}

            {error ? (
              <Alert
                type="error"
                showIcon
                className="bulk-upload-alert"
                title="File not accepted"
                description={error}
              />
            ) : null}
          </section>

          <aside className="bulk-upload-side">
            <section className="bulk-upload-card bulk-upload-card--template">
              <div className="bulk-upload-card__head">
                <span className="bulk-upload-step">Step 1</span>
                <h2 className="bulk-upload-card__title">{templateTitle}</h2>
              </div>
              <p className="bulk-upload-side__hint">{templateHint}</p>
              <Button
                block
                icon={<DownloadOutlined />}
                loading={downloading}
                onClick={handleDownload}
                className="bulk-upload-template-btn"
              >
                {templateLabel}
              </Button>
            </section>

            {instructions.length ? (
              <section className="bulk-upload-card bulk-upload-card--rules">
                <h2 className="bulk-upload-card__title">Before you upload</h2>
                <ul className="bulk-upload-rules">
                  {instructions.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              </section>
            ) : null}

            {extraSidebar}
          </aside>
        </div>

        {result && renderResult ? (
          <div className="bulk-upload-result">{renderResult(result, { reset })}</div>
        ) : null}
      </div>
    </div>
  );
}
